import { z } from "zod";
import {
  MAX_DISCOUNT_PERCENT,
  PACKAGE_CATEGORIES,
  isDiscountWithinBand,
  packageInputSchema,
  slugify,
  type Locale,
  type Package,
  type PackageCategory,
  type PackageInput,
  type PackageItem,
  type PackageUpdate,
  type ProductStatus,
} from "@nh/shared";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El formulario del paquete: de lo que se escribe a lo que se guarda
 *
 * Mismo trato que el del producto, y por las mismas dos razones:
 *
 *  · Los CAMPOS son texto, porque eso es lo que produce un `<input>`. Un
 *    formulario que finge tener números manda `NaN` la primera vez que
 *    alguien borra el contenido de un campo.
 *  · Con la MISMA FORMA que el contrato (`translations.es.name`,
 *    `items.0.productId`), porque el esquema termina en
 *    `.pipe(packageInputSchema)`: cuando el contrato rechaza algo, la
 *    ruta del error coincide letra por letra con el nombre del campo y
 *    react-hook-form lo pinta en el control correcto.
 *
 * ── Lo que hace distinto a un paquete ───────────────────────────────
 * NO HAY CAMPO DE PRECIO, y no es un olvido. `packageInputSchema` no
 * tiene `prices` ni `listPrices`: lo que se escribe es
 * `discountPercent`, y el precio lo deriva el servidor sumando los
 * productos del paquete. Un precio tecleado aparte del porcentaje se
 * desincroniza en cuanto alguien toca uno de los dos, y entonces la
 * tienda promete un ahorro que no existe.
 *
 * Lo que sí hay es una VISTA PREVIA de ese precio, en
 * `packagePricing.ts`, que hace la misma cuenta con las mismas
 * funciones del contrato para poder enseñarla mientras se edita.
 * ═══════════════════════════════════════════════════════════════════
 */

// ── Los valores tal y como viven en los controles ───────────────────

export interface PackageContentFormValues {
  name: string;
  tagline: string;
  description: string;
}

/** Una fila de la lista de productos: qué producto y cuántos. */
export interface PackageItemValues {
  productId: string;
  quantity: string;
}

export interface PackageFormValues {
  slug: string;
  translations: Record<Locale, PackageContentFormValues>;
  category: string;
  items: PackageItemValues[];
  discountPercent: string;
  /**
   * Precio y puntos FIJOS (Anahí, 14-sep-2026), en lo que se teclea:
   * "4000.00" y "150". Vacíos = el paquete se calcula como siempre.
   */
  fixedPriceMXN: string;
  fixedPriceUSD: string;
  fixedVolumePoints: string;
  franchiseeDiscountPercent: string;
  /**
   * La caja real (Anahí, 21-sep-2026), en lo que se teclea: "900",
   * "30", "20", "15". Vacío = viaja la suma de sus productos, que la
   * pantalla enseña como propuesta.
   */
  shipping: {
    weightGrams: string;
    lengthCm: string;
    widthCm: string;
    heightCm: string;
  };
  /** Solo cabe en la categoría mayorista; el servidor lo vuelve a exigir. */
  includesFranchise: boolean;
  imageUrl: string;
  status: string;
  sortOrder: string;
}

// ── Los valores de partida ──────────────────────────────────────────

const EMPTY_CONTENT: PackageContentFormValues = {
  name: "",
  tagline: "",
  description: "",
};

/**
 * Con qué descuento nace un paquete.
 *
 * El 10 % está DENTRO de las dos bandas (15 % personal, 30 %
 * franquiciatario), así que cambiar de categoría con el valor de partida
 * nunca dispara un aviso de banda antes de que nadie haya escrito nada.
 * Un valor de partida que ya está en rojo enseña a ignorar el rojo.
 */
export const DEFAULT_DISCOUNT_PERCENT = 10;

export function emptyPackageFormValues(): PackageFormValues {
  return {
    slug: "",
    translations: { es: { ...EMPTY_CONTENT }, en: { ...EMPTY_CONTENT } },
    category: "personal",
    // Una fila abierta: un paquete sin productos no existe, y empezar
    // con la lista vacía obliga a descubrir el botón antes de entender
    // de qué va la pantalla.
    items: [{ productId: "", quantity: "1" }],
    discountPercent: String(DEFAULT_DISCOUNT_PERCENT),
    fixedPriceMXN: "",
    fixedPriceUSD: "",
    fixedVolumePoints: "",
    franchiseeDiscountPercent: "",
    shipping: { weightGrams: "", lengthCm: "", widthCm: "", heightCm: "" },
    includesFranchise: false,
    imageUrl: "",
    status: "draft",
    sortOrder: "0",
  };
}

// ── De lo guardado a lo que se edita ────────────────────────────────

function numberToInput(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

/** De centavos a lo que se escribe en el campo: "4000.00", sin símbolo. */
function centsToInput(cents: number | undefined): string {
  return cents === undefined ? "" : (cents / 100).toFixed(2);
}

export function packageToFormValues(pkg: Package): PackageFormValues {
  return {
    slug: pkg.slug,
    translations: {
      es: {
        name: pkg.translations.es.name,
        tagline: pkg.translations.es.tagline ?? "",
        description: pkg.translations.es.description ?? "",
      },
      en: {
        name: pkg.translations.en?.name ?? "",
        tagline: pkg.translations.en?.tagline ?? "",
        description: pkg.translations.en?.description ?? "",
      },
    },
    category: pkg.category,
    items: pkg.items.map((item) => ({
      productId: item.productId,
      quantity: String(item.quantity),
    })),
    discountPercent: String(pkg.discountPercent),
    fixedPriceMXN: centsToInput(pkg.fixedPrices?.["MXN"]),
    fixedPriceUSD: centsToInput(pkg.fixedPrices?.["USD"]),
    fixedVolumePoints: numberToInput(pkg.fixedVolumePoints),
    franchiseeDiscountPercent: numberToInput(pkg.franchiseeDiscountPercent),
    shipping: {
      weightGrams: numberToInput(pkg.shipping?.weightGrams),
      lengthCm: numberToInput(pkg.shipping?.lengthCm),
      widthCm: numberToInput(pkg.shipping?.widthCm),
      heightCm: numberToInput(pkg.shipping?.heightCm),
    },
    includesFranchise: pkg.includesFranchise ?? false,
    imageUrl: pkg.imageUrl ?? "",
    status: pkg.status,
    sortOrder: String(pkg.sortOrder),
  };
}

// ── Leer lo que se escribió ─────────────────────────────────────────

/** `undefined` si el campo está vacío; el texto limpio si no. */
function textOrUndefined(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** `undefined` si está vacío; el entero si se entiende; `null` si no. */
function readInteger(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * Un precio tecleado ("4000" o "4000.50") a centavos enteros.
 * `undefined` si está vacío; `null` si no se entiende.
 */
function readCents(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "") return undefined;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * 100);
}

/** `undefined` si está vacío; el número (con decimales) si se entiende; `null` si no. */
function readDecimal(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "") return undefined;
  if (!/^\d*\.?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function anyText(...values: string[]): boolean {
  return values.some((value) => value.trim() !== "");
}

export function isPackageCategory(value: string): value is PackageCategory {
  return (PACKAGE_CATEGORIES as readonly string[]).includes(value);
}

/**
 * Las líneas del paquete que YA cuentan, para la vista previa.
 *
 * Las filas a medio llenar —sin producto elegido, o con una cantidad que
 * no es un número— se descartan: no cambian lo que cuesta el paquete, y
 * dejarlas entrar pondría el total en `NaN` mientras alguien teclea.
 */
export function readPackageItems(rows: readonly PackageItemValues[]): PackageItem[] {
  const items: PackageItem[] = [];
  for (const row of rows) {
    const productId = row.productId.trim();
    if (productId === "") continue;
    const quantity = readInteger(row.quantity);
    if (typeof quantity !== "number" || quantity < 1) continue;
    items.push({ productId, quantity });
  }
  return items;
}

/** El mapa de precios fijos que se tecleó: solo las monedas con algo escrito. */
function readFixedPrices(values: { fixedPriceMXN: string; fixedPriceUSD: string }): Record<string, number> {
  const out: Record<string, number> = {};
  const mxn = readCents(values.fixedPriceMXN);
  const usd = readCents(values.fixedPriceUSD);
  if (typeof mxn === "number") out["MXN"] = mxn;
  if (typeof usd === "number") out["USD"] = usd;
  return out;
}

/** El descuento que se está escribiendo, o `null` mientras no se entienda. */
export function readDiscountPercent(raw: string): number | null {
  const value = readInteger(raw);
  return typeof value === "number" ? value : null;
}

// ── El esquema de lo que se escribe ─────────────────────────────────

const contentValuesSchema = z.object({
  name: z.string(),
  tagline: z.string(),
  description: z.string(),
});

const formValuesSchema = z.object({
  slug: z.string(),
  translations: z.object({ es: contentValuesSchema, en: contentValuesSchema }),
  category: z.string(),
  items: z.array(z.object({ productId: z.string(), quantity: z.string() })),
  discountPercent: z.string(),
  fixedPriceMXN: z.string(),
  fixedPriceUSD: z.string(),
  fixedVolumePoints: z.string(),
  franchiseeDiscountPercent: z.string(),
  shipping: z.object({
    weightGrams: z.string(),
    lengthCm: z.string(),
    widthCm: z.string(),
    heightCm: z.string(),
  }),
  includesFranchise: z.boolean(),
  imageUrl: z.string(),
  status: z.string(),
  sortOrder: z.string(),
});

type Ctx = z.core.$RefinementCtx<z.infer<typeof formValuesSchema>>;

function issue(ctx: Ctx, path: (string | number)[], message: string): void {
  ctx.addIssue({ code: "custom", path, message });
}

/** El aviso de que el descuento se sale de su banda, o `null` si cabe. */
export function discountBandIssue(
  percent: number,
  category: PackageCategory,
): string | null {
  if (isDiscountWithinBand(percent, category)) return null;
  return `El descuento de esta categoría no puede pasar del ${MAX_DISCOUNT_PERCENT[category]}%`;
}

/**
 * El esquema del formulario: lee lo escrito, avisa donde toca, y entrega
 * lo que el contrato espera.
 *
 * Termina en `.pipe(packageInputSchema)` a propósito: lo que define el
 * negocio —cuánto mide un nombre, qué forma tiene un slug, entre qué
 * valores va el descuento— no se reimplementa aquí. Lo que sí vive aquí
 * es lo que el contrato NO puede comprobar solo: que el descuento quepa
 * en la banda de SU categoría, porque eso cruza dos campos.
 */
export const packageFormSchema = formValuesSchema
  .superRefine((values, ctx) => {
    // ── Los productos del paquete ──────────────────────────────────
    const seen = new Set<string>();
    values.items.forEach((row, index) => {
      const productId = row.productId.trim();
      if (productId === "") {
        issue(ctx, ["items", index, "productId"], "Elige un producto");
      } else if (seen.has(productId)) {
        /*
         * El mismo producto dos veces no es un error del contrato, pero
         * sí un error de quien lo arma: para llevar tres frascos está la
         * cantidad. Dos filas del mismo producto se contradicen en
         * cuanto alguien edita una sola.
         */
        issue(ctx, ["items", index, "productId"], "Ese producto ya está en el paquete");
      }
      seen.add(productId);

      const quantity = readInteger(row.quantity);
      if (quantity === null || quantity === undefined || quantity < 1 || quantity > 99) {
        issue(ctx, ["items", index, "quantity"], "La cantidad va de 1 a 99");
      }
    });

    // ── El descuento y su banda ────────────────────────────────────
    const percent = readInteger(values.discountPercent);
    if (percent === null || percent === undefined) {
      issue(ctx, ["discountPercent"], "Escribe el descuento como un número entero");
    } else if (isPackageCategory(values.category)) {
      const bandMessage = discountBandIssue(percent, values.category);
      if (bandMessage) issue(ctx, ["discountPercent"], bandMessage);
    }

    const extra = readInteger(values.franchiseeDiscountPercent);
    if (extra === null || (typeof extra === "number" && (extra < 0 || extra > 50))) {
      issue(ctx, ["franchiseeDiscountPercent"], "El porcentaje va de 0 a 50");
    }

    // ── Lo fijo: vacío es "calcular"; escrito, tiene que entenderse ──
    if (readCents(values.fixedPriceMXN) === null) {
      issue(ctx, ["fixedPriceMXN"], "Escribe el precio en pesos, con hasta dos decimales");
    }
    if (readCents(values.fixedPriceUSD) === null) {
      issue(ctx, ["fixedPriceUSD"], "Escribe el precio en dólares, con hasta dos decimales");
    }
    const fixedVp = readInteger(values.fixedVolumePoints);
    if (fixedVp === null || (typeof fixedVp === "number" && fixedVp < 0)) {
      issue(ctx, ["fixedVolumePoints"], "Los puntos son un entero, 0 o más");
    }

    // ── La caja: vacío es "la suma"; escrito, tiene que entenderse ──
    const weight = readInteger(values.shipping.weightGrams);
    if (weight === null || (typeof weight === "number" && weight < 0)) {
      issue(ctx, ["shipping", "weightGrams"], "El peso va en gramos enteros, 0 o más");
    }
    for (const side of ["lengthCm", "widthCm", "heightCm"] as const) {
      const value = readDecimal(values.shipping[side]);
      if (value === null || (typeof value === "number" && value < 0)) {
        issue(ctx, ["shipping", side], "La medida va en centímetros, 0 o más");
      }
    }

    // ── "Franquicia incluida" solo cabe en un mayorista ─────────────
    // El servidor lo vuelve a exigir; aquí se ataja antes de enviar.
    if (values.includesFranchise && values.category === "personal") {
      issue(
        ctx,
        ["includesFranchise"],
        "Solo un paquete mayorista (negocio digital) puede incluir la franquicia",
      );
    }

    const sortOrder = readInteger(values.sortOrder);
    if (sortOrder === null || sortOrder === undefined) {
      issue(ctx, ["sortOrder"], "El orden es un número entero");
    }
  })
  /*
   * El tipo de salida se ANOTA como la entrada del contrato en vez de
   * dejarlo inferir. Sin la anotación, TypeScript infiere que `tagline`
   * y `description` son propiedades OBLIGATORIAS de tipo
   * `string | undefined` —porque `textOrUndefined` siempre devuelve la
   * clave— y el `.pipe()` de abajo deja de compilar: el contrato las
   * declara opcionales, y "obligatoria que puede ser undefined" y
   * "opcional" no son el mismo tipo para la comprobación de varianza
   * que hace zod entre los dos esquemas encadenados.
   */
  .transform((values): z.input<typeof packageInputSchema> => {
    const es = values.translations.es;
    const en = values.translations.en;

    return {
      slug: values.slug.trim(),
      translations: {
        es: {
          name: es.name.trim(),
          tagline: textOrUndefined(es.tagline),
          description: textOrUndefined(es.description),
        },
        /*
         * El inglés viaja solo si hay ALGO escrito. Un objeto de
         * cadenas vacías se guardaría como una traducción existente y
         * vacía, y `contentFor` dejaría de caer al español: la tienda en
         * inglés enseñaría un paquete sin nombre.
         */
        en: anyText(en.name, en.tagline, en.description)
          ? {
              name: textOrUndefined(en.name),
              tagline: textOrUndefined(en.tagline),
              description: textOrUndefined(en.description),
            }
          : undefined,
      },
      /*
       * El `<select>` produce texto, y TypeScript no puede saber que ese
       * texto es una de las dos categorías. La afirmación es segura
       * porque lo siguiente que pasa es `.pipe(packageInputSchema)`: si
       * alguien manipula el desplegable y manda "mayorista", el contrato
       * lo rechaza con un error en el campo `category`.
       */
      category: values.category as PackageCategory,
      items: readPackageItems(values.items),
      // `NaN` cuando no se entiende lo escrito: el aviso bueno ya lo puso
      // el refinamiento de arriba, y el contrato lo rechaza igual.
      discountPercent: readInteger(values.discountPercent) ?? Number.NaN,
      /*
       * Lo fijo viaja como mapa por moneda; una moneda vacía no va. Un
       * mapa sin monedas se manda igual (`{}`): al EDITAR es la orden de
       * volver al cálculo, y al crear el servidor lo trata como ausente.
       */
      fixedPrices: readFixedPrices(values),
      fixedVolumePoints: readInteger(values.fixedVolumePoints) ?? undefined,
      franchiseeDiscountPercent: readInteger(values.franchiseeDiscountPercent) ?? undefined,
      // La caja real; los huecos NO viajan, y los llena la suma al
      // pedir la guía. Al editar, `toPackageUpdate` convierte el vacío
      // total en `null` para que el servidor la borre.
      shipping: readShipping(values.shipping),
      /*
       * Viaja SIEMPRE que la casilla esté puesta, y también el `false`
       * explícito al editar: en un PATCH, mandar `false` es la única
       * forma de DESMARCAR un paquete que la tenía (el servicio lo
       * traduce a `$unset`).
       */
      includesFranchise: values.includesFranchise,
      imageUrl: values.imageUrl.trim(),
      // Mismo caso que `category`: el contrato es quien lo comprueba.
      status: values.status as ProductStatus,
      sortOrder: readInteger(values.sortOrder) ?? 0,
    };
  })
  .pipe(packageInputSchema);

/**
 * Al editar, el slug no viaja: cambiarlo rompería los enlaces compartidos.
 *
 * Y los puntos fijos vacíos viajan como `null`: JSON tira los
 * `undefined`, y sin el `null` el servidor no sabría que se borraron.
 */
export function toPackageUpdate(input: PackageInput): PackageUpdate {
  const { slug: _slug, ...rest } = input;
  return {
    ...rest,
    fixedVolumePoints: rest.fixedVolumePoints ?? null,
    shipping: rest.shipping ?? null,
  };
}

/** Solo los campos escritos; `undefined` cuando los cuatro están vacíos. */
function readShipping(values: PackageFormValues["shipping"]): PackageInput["shipping"] {
  const shipping = {
    weightGrams: readInteger(values.weightGrams) ?? undefined,
    lengthCm: readDecimal(values.lengthCm) ?? undefined,
    widthCm: readDecimal(values.widthCm) ?? undefined,
    heightCm: readDecimal(values.heightCm) ?? undefined,
  };
  return Object.values(shipping).some((value) => value !== undefined) ? shipping : undefined;
}

// ── Qué idioma va completo ──────────────────────────────────────────

export type LocaleFillState = "complete" | "partial" | "empty";

/**
 * Cómo está cada idioma SIN tener que abrir su pestaña.
 *
 * `partial` es el estado que importa: alguien escribió el eslogan en
 * inglés y se fue sin el nombre. Sin este aviso, el paquete se publica y
 * la tienda en inglés enseña un nombre en español al lado de un eslogan
 * en inglés, y nadie se entera hasta que lo ve un cliente.
 */
export function localeFillState(content: PackageContentFormValues): LocaleFillState {
  const filled = [content.name, content.tagline, content.description].filter(
    (value) => value.trim() !== "",
  ).length;
  if (filled === 0) return "empty";
  return content.name.trim().length >= 2 ? "complete" : "partial";
}

/** La sugerencia de slug a partir del nombre en español. */
export function suggestSlug(name: string): string {
  return slugify(name);
}
